import React, { useEffect, useMemo, useState } from 'react';
import { getActiveTheme, applyThemeVars, SeasonalTheme } from '../../config/seasonalThemes.ts';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { API_URL } from '../../config/api.ts';

// Import all theme CSS files
import '../../styles/themes/valentine.css';
import '../../styles/themes/easter.css';
import '../../styles/themes/april-fools.css';

const SeasonalThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<SeasonalTheme | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!auth.currentUser);

  // Écouter l'état de connexion
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setIsLoggedIn(!!user));
    return () => unsub();
  }, []);

  useEffect(() => {
    let active = getActiveTheme();
    // Poisson d'avril : surprise → cacher le thème tant que l'utilisateur n'est pas connecté
    if (active?.id === 'april-fools' && !isLoggedIn) {
      active = null;
    }
    setTheme(active);
    applyThemeVars(active);

    const interval = setInterval(() => {
      let current = getActiveTheme();
      if (current?.id === 'april-fools' && !isLoggedIn) {
        current = null;
      }
      setTheme((prev: SeasonalTheme | null) => {
        if (prev?.id !== current?.id) {
          applyThemeVars(current);
          return current;
        }
        return prev;
      });
    }, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
      applyThemeVars(null);
    };
  }, [isLoggedIn]);

  // Déverrouillage automatique de la décoration Poisson d'Avril
  useEffect(() => {
    if (theme?.id !== 'april-fools') return;
    if (sessionStorage.getItem('af-deco-claimed')) return;

    const user = auth.currentUser;
    if (!user) return;

    user.getIdToken().then(token =>
      fetch(`${API_URL}/api/april-fools/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(() => sessionStorage.setItem('af-deco-claimed', '1'))
        .catch(() => {})
    );
  }, [theme?.id]);

  // Lettres mélangées au hover sur les pseudos avec déco Poisson d'Avril
  // Actif toute l'année — la déco est permanente
  useEffect(() => {

    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const scramble = (nameEl: HTMLElement) => {
      if (nameEl.dataset.scrambled) return;
      const original = nameEl.textContent || '';
      nameEl.dataset.original = original;
      nameEl.dataset.scrambled = '1';

      const chars = original.split('');
      for (let i = chars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
      }

      nameEl.innerHTML = chars
        .map((c, i) => {
          const deg = Math.random() > 0.5 ? 180 : 0;
          return `<span class="af-letter" style="--af-r:${deg}deg;animation:af-letter-in 0.15s ease-out ${i * 20}ms both">${
            c === ' ' ? '&nbsp;' : esc(c)
          }</span>`;
        })
        .join('');
    };

    const restore = (nameEl: HTMLElement) => {
      if (!nameEl.dataset.scrambled) return;
      const original = nameEl.dataset.original || '';
      nameEl.style.transition = 'opacity 0.1s ease';
      nameEl.style.opacity = '0';
      setTimeout(() => {
        nameEl.textContent = original;
        nameEl.style.opacity = '1';
        setTimeout(() => { nameEl.style.transition = ''; nameEl.style.opacity = ''; }, 100);
        delete nameEl.dataset.scrambled;
        delete nameEl.dataset.original;
      }, 100);
    };

    const handleOver = (e: MouseEvent) => {
      const nameEl = (e.target as Element).closest<HTMLElement>('.deco-name-text');
      if (!nameEl?.closest('.player-name.deco-april-fools')) return;
      scramble(nameEl);
    };

    const handleOut = (e: MouseEvent) => {
      const nameEl = (e.target as Element).closest<HTMLElement>('.deco-name-text');
      if (!nameEl?.closest('.player-name.deco-april-fools')) return;
      if (nameEl.contains(e.relatedTarget as Node)) return;
      restore(nameEl);
    };

    document.addEventListener('mouseover', handleOver);
    document.addEventListener('mouseout', handleOut);
    return () => {
      document.removeEventListener('mouseover', handleOver);
      document.removeEventListener('mouseout', handleOut);
    };
  }, []);

  // Boutons qui s'esquivent au hover (Poisson d'Avril uniquement)
  // La distance escalade à chaque tentative — mode "oulala" garanti après 3 essais
  useEffect(() => {
    if (theme?.id !== 'april-fools') return;

    const attempts = new WeakMap<HTMLElement, number>();

    const handleOver = (e: MouseEvent) => {
      const target = (e.target as Element).closest<HTMLElement>(
        '.game-button-v2.primary, .card-button-v2.primary'
      );
      if (!target) return;

      const n = (attempts.get(target) ?? 0) + 1;
      attempts.set(target, n);

      // 1ère tentative : gros sursaut. Ensuite : petit dodge jouable.
      const range = n === 1 ? 180 : 40;
      const dx = Math.round((Math.random() < 0.5 ? -1 : 1) * (range * 0.7 + Math.random() * range * 0.5));
      const dy = Math.round((Math.random() < 0.5 ? -1 : 1) * (range * 0.4 + Math.random() * range * 0.3));

      target.style.transition = n === 1 ? 'transform 0.2s cubic-bezier(0.34,1.56,0.64,1)' : 'transform 0.13s ease';
      target.style.transform = `translate(${dx}px, ${dy}px)`;
    };

    const handleOut = (e: MouseEvent) => {
      const target = (e.target as Element).closest<HTMLElement>(
        '.game-button-v2.primary, .card-button-v2.primary'
      );
      if (!target) return;
      target.style.transform = '';
    };

    document.addEventListener('mouseover', handleOver);
    document.addEventListener('mouseout', handleOut);
    return () => {
      document.removeEventListener('mouseover', handleOver);
      document.removeEventListener('mouseout', handleOut);
    };
  }, [theme?.id]);

  return (
    <>
      {children}
      {theme?.particles?.enabled && <FloatingParticles theme={theme} />}
      {theme?.id === 'april-fools' && (
        <>
          <AprilFoolsFish />
          <AprilFoolsError />
        </>
      )}
    </>
  );
};

/* ── Floating particles ──────────────────────────────── */
const FloatingParticles: React.FC<{ theme: SeasonalTheme }> = React.memo(({ theme }) => {
  const particles = useMemo(() => {
    if (!theme.particles) return [];
    const { emojis, count, speed = 1 } = theme.particles;
    return Array.from({ length: count }, (_, i) => {
      const emoji = emojis[i % emojis.length];
      const left = Math.random() * 100;
      const duration = (12 + Math.random() * 18) / speed;
      const delay = -(Math.random() * duration);
      const size = 0.8 + Math.random() * 1;
      const drift = -30 + Math.random() * 60;
      const maxOpacity = 0.3 + Math.random() * 0.45;
      const midScale = 0.8 + Math.random() * 0.4;
      return {
        key: `${theme.id}-p-${i}`,
        emoji,
        style: {
          left: `${left}%`,
          '--duration': `${duration}s`,
          '--delay': `${delay}s`,
          '--size': `${size}rem`,
          '--drift': `${drift}px`,
          '--max-opacity': maxOpacity,
          '--mid-scale': midScale,
        } as React.CSSProperties,
      };
    });
  }, [theme.id, theme.particles]);

  return (
    <div className="seasonal-particles" aria-hidden="true">
      {particles.map(p => (
        <span key={p.key} className="seasonal-particle" style={p.style}>
          {p.emoji}
        </span>
      ))}
    </div>
  );
});
FloatingParticles.displayName = 'FloatingParticles';

/* ── Poisson collé dans le dos ───────────────────────── */
const AprilFoolsFish: React.FC = () => (
  <div className="april-fools-fish" aria-hidden="true">
    <div className="april-fools-fish-paper">
      <span className="april-fools-fish-emoji">🐟</span>
      <span className="april-fools-fish-label">Poisson d'Avril</span>
    </div>
  </div>
);

/* ── Faux message de suspension + révélation ─────────── */
const CONFETTI_COLORS = ['#fde68a', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444', '#f97316'];

const AprilFoolsError: React.FC = () => {
  const [phase, setPhase] = useState<'error' | 'reveal' | 'gone'>(() =>
    sessionStorage.getItem('af-shown') ? 'gone' : 'error'
  );

  const confetti = useMemo(() =>
    Array.from({ length: 35 }, (_, i) => ({
      key: i,
      left: Math.random() * 100,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 0.6,
      duration: 0.9 + Math.random() * 0.7,
      size: 6 + Math.round(Math.random() * 8),
      round: Math.random() > 0.5,
    })),
  []);

  useEffect(() => {
    if (phase === 'gone') return;
    const t1 = setTimeout(() => setPhase('reveal'), 7000);
    const t2 = setTimeout(() => {
      setPhase('gone');
      sessionStorage.setItem('af-shown', '1');
    }, 10500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (phase === 'gone') return null;

  return (
    <div className="af-overlay" aria-live="assertive">
      {phase === 'error' ? (
        <div className="af-card af-card--error">
          <div className="af-icon">🚨</div>
          <div className="af-title af-title--red">Compte suspendu</div>
          <p className="af-body">Votre compte a été suspendu pour tricherie.</p>
          <p className="af-sub">Motif : trop de bonnes réponses suspectes</p>
          <div className="af-bar"><div className="af-bar-fill" /></div>
        </div>
      ) : (
        <div className="af-card af-card--reveal">
          {confetti.map(c => (
            <div
              key={c.key}
              className="af-confetti"
              style={{
                left: `${c.left}%`,
                background: c.color,
                width: c.size,
                height: c.size,
                borderRadius: c.round ? '50%' : '2px',
                animationDuration: `${c.duration}s`,
                animationDelay: `${c.delay}s`,
              }}
            />
          ))}
          <div className="af-icon af-icon--bounce">🐟</div>
          <div className="af-title af-title--gold">Poisson d'Avril !</div>
          <p className="af-body">Tu t'es fait avoir ! 😄</p>
        </div>
      )}
    </div>
  );
};

export default SeasonalThemeProvider;
